import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import * as _ from 'lodash';
import { withTranslation } from 'react-i18next';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { toastr } from 'react-redux-toastr';

import Label_T from 'common/typography/Label/Label';
import * as assetDetailsActions from 'home/AssetHierarchy/actions/assetDetails.actions';
import * as utils from 'common/utils';
import { OVERLAY_POINTS_LIMIT } from 'common/constants';

// Annotations imports
import { setAnnotationsVisible } from 'home/Annotations/actions/annotations.actions';
import { getVisible } from 'home/Annotations/selectors/annotations.selectors';

import AlertPrompt from 'common/components/organisms/AlertPrompt';
import * as hierarchyUpdateActions from 'home/AssetHierarchy/actions/hierarchy.actions';
import DeleteIconSvg from 'common/images/BearingModal/DeleteIconSvg';
import { getTagAmplitudeTypes } from 'common/components/Chart/utils/helpers';
import FlexContainer from '../../atoms/FlexContainer';
import { mapFeatureType, measurementOptions, spectraOptions, getBinFilterOptions } from '../../../charts/constants';
import { chartFeatureDisplayName, chartAmpOrFeatureDisplayName } from '../../../charts/chartI18n';
import OutsideAlerter from '../../../OutsideAlerter';
import MoreIconSvg from '../../../images/MoreIconSvg';
import RefreshSvg from '../../../images/RefreshSvg';
import DownloadSvg from '../../../images/DownloadSvg';
import AddNoteSvg from '../../../images/NoteSvg';
import SpeedFilterSvg from '../../../images/SpeedFilterSvg';
import EditIcon from '../../../images/BearingModal/EditIcon';
import SplitIconSvg from '../../../images/SplitIconSvg';
import {
  ToggleOff as FaToggleOff,
  ToggleOn as FaToggleOn,
} from '../../../images/FaIcons';
import FilterItem from '../../atoms/FilterItem';
import Filter_T from '../../molecules/Filter';
import ChartHeaderRowContainer from '../atoms/HeaderRowContainer';
import InputField from '../atoms/InputField';
import SpeedFilter from '../organisms/SpeedFilter';
import H4_T from '../../../typography/H4/H4';
import CalendarFilter from './CalendarFilter';
import { Checkbox } from '../../atoms';


const Header = styled(Label_T)`
  color: grey;
  padding: 0.5em 0 0 0.7em;
`;

const OptionsContainer = styled(FlexContainer)`
  justify-content: space-between;
  height: 44px;
  border-bottom: 1px solid #EFF0EE;
  box-sizing: border-box;
  padding-right: 25px;
`;

const BtnContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  outline: none;
  border: none;
  padding: 0 0.3em;
  margin: 0 0.3em;
  z-index: 1;
  height: inherit;
  align-items: center;
  cursor: pointer;
  & * {
    cursor: pointer;
  }
  ${MoreIconSvg} {
    transform: rotate(90deg) translateX(6px);

  }
  ${props => props.disabled && `
    cursor: not-allowed;
    & * {
      cursor: not-allowed;
    }
  `}
`;

const Filter = styled(Filter_T)`
  height: 20px;
  border-right: 1px solid grey;
  padding-right: 1em;
  z-index: 3;
  width: ${props => props.setWidth};
`;

const BinFilter = styled(Filter_T)`
  height: 20px;
  z-index: 3;
  width: ${props => props.setWidth};
`;

const BorderContainer = styled.div`
  height: 20px;
  border-right: 1px solid grey;
`;

const SpeedFilterCont = styled.div`
  cursor: pointer;
  padding: 0 0.3em;
  margin: 0 0.3em;
  border-right: 1px solid grey;
  padding-right: 0.7em;
`;

const Label = styled(Label_T)`
  white-space: nowrap;
  ${props => props.padding && `padding: ${props.padding}`};
  ${props => props.active && `color: ${props.theme.primaryColor}`};
  ${props => props.disabled && `color: ${props.theme.colors.greyD}`};
`;

const H4 = styled(H4_T)`
  color: ${props => props.active ? props.theme.primaryColor : props.theme.colors.black};
`;

const ToggleOn = styled(FaToggleOn)`
  color: ${props => props.theme.primaryColor};
  font-size: 1.5em;
  cursor: pointer;
  ${props => props.disabled && `
    cursor: not-allowed;
    & * {
      cursor: not-allowed;
    }
  `}
`;

const ToggleOff = styled(FaToggleOff)`
  color: ${props => props.theme.colors.greyD};
  font-size: 1.5em;
  cursor: pointer;
  ${props => props.disabled && `
    cursor: not-allowed;
    & * {
      cursor: not-allowed;
    }
  `}
`;

const ListOption = styled.span`
  margin-left: 10px;
  color: ${props => props.selected ? props.theme.colors.black : props.theme.colors.greyXD};
  white-space: pre;
`;

const SpectraInputField = styled(InputField)`
  margin-right: 0.7em;
  .ui.fluid.selection.dropdown {
    padding: .5em 2.1em .5em 1em;
    min-height: 2em;
  }
  .icon {
    padding: 0.6em !important;
  }
`;


const getSvg = (icon, selected, theme) => {
  const fill = selected ? theme.primaryColor : '#3C3D3A';
  switch (icon) {
    case 'refresh':
      return <RefreshSvg fill={fill} />;
    case 'download':
      return <DownloadSvg fill={fill} />;
    case 'addNote':
      return <AddNoteSvg fill={fill} />;
    case 'speedFilter':
      return <SpeedFilterSvg fill={fill} />;
    case 'editChart':
      return <EditIcon fill={fill} />;
    default:
      return null;
  }
};

class ChartOptions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ampOptionsExpanded: false,
      measurementOptionsExpanded: false,
      featureOptionsExpanded: false,
      binFilterExpanded: false,
      speedFilterExpanded: false,
      deleteTagModal: false,
      tagToDelete: {},
      showOutliers: !!assetDetailsActions.getChartSettingsFromSession().showOutliers,
      tagFeatures: [],
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props
      && this.props.tag
      && this.props.tag.features
      && !_.isEqual(prevProps, this.props)
    ) this.updateTagFeatures();
  }

  updateTagFeatures = () => {
    if (this.props.tag.features.length > 0) {
      let features = this.props.tag.features;

      if (this.props.tag.spectrum_features && this.props.tag.spectrum_features.length > 0) {
        const spectrum_features = this.props.tag.spectrum_features.filter(feature => !['demod_1', 'demod_2'].includes(feature));
        features = [...this.props.tag.features, ...spectrum_features];
      }

      const broadbandFeatures = [];
      const narrowbandFeatures = [];

      const { t } = this.props;
      features.forEach((feature) => {
        if (mapFeatureType[feature] === 'Broadband') {
          broadbandFeatures.push({
            text: chartFeatureDisplayName(t, feature),
            value: feature
          });
        } else {
          narrowbandFeatures.push({
            text: chartFeatureDisplayName(t, feature),
            value: feature
          });
        }
      });

      const tagFeatures = [];
      if (broadbandFeatures.length) {
        tagFeatures.push({
          header: t('machineDashboard.health.charts.featureGroups.broadband'),
          children: broadbandFeatures
        });
      }
      if (narrowbandFeatures.length) {
        tagFeatures.push({
          header: t('machineDashboard.health.charts.featureGroups.narrowband'),
          children: narrowbandFeatures
        });
      }
      this.setState({ tagFeatures });
    }
  }

  selectDeleteInactiveTag = (tag_id) => {
    this.setState({
      deleteTagModal: true,
      tagToDelete: { tag_id }
    });
  }

  deleteTag = (tag) => {
    this.setState({
      deleteTagModal: false,
      tagToDelete: {}
    });

    this.props.hierarchyUpdateActions.deleteTags({ tag_id: tag.tag_id, account_id: this.props.currentUser.account_id }).then(
      (res) => {
        toastr.success(res.message);
      },
      (error) => {
        toastr.error(error.message);
      }
    );
  }

  toggleShowOutliers = () => {
    this.setState(prevState => ({
      showOutliers: !prevState.showOutliers
    }));
    assetDetailsActions.toggleShowSettingInSession('showOutliers');
    this.props.refreshChart();
  }

  toggleShowAnnotations = () => {
    const { annotationsVisible, setAnnotationsVisible } = this.props;
    // Persist preference in session (mirror Outliers persistence)
    assetDetailsActions.toggleShowSettingInSession('showAnnotations');
    setAnnotationsVisible(!annotationsVisible);
  }

  toggleAmpOptionsExpand = () => {
    this.setState(prevState => ({
      ampOptionsExpanded: !prevState.ampOptionsExpanded
    }));
  }

  ampOptionSelected = (amp_type) => {
    this.setState({ ampOptionsExpanded: false });
    this.props.getChartData({ amp_type });
  }

  toggleMeasurementOptionsExpand = () => {
    this.setState(prevState => ({
      measurementOptionsExpanded: !prevState.measurementOptionsExpanded
    }));
  }

  measurementOptionSelected = (measurement_type) => {
    this.setState({ measurementOptionsExpanded: false });
    this.props.getChartData({ measurement_type });
  }

  toggleFeatureOptionsExpand = () => {
    this.setState(prevState => ({
      featureOptionsExpanded: !prevState.featureOptionsExpanded
    }));
  }

  featureSelected = (feature) => {
    this.setState({ featureOptionsExpanded: false });
    this.props.getChartData({ feature });
  }

  toggleSpeedFilter = () => {
    this.setState(prevState => ({
      speedFilterExpanded: !prevState.speedFilterExpanded
    }));
  }

  onSaveSpeedFilter = (filterMin, filterMax) => {
    this.setState({ speedFilterExpanded: false });
    this.props.setSpeedFilterValues(filterMin, filterMax);
  }

  toggleBinFilter = () => {
    this.setState(prevState => ({
      binFilterExpanded: !prevState.binFilterExpanded
    }));
  }

  speedBinSelected = (bin_no, selected) => {
    let bin_nos = [];
    if (selected) {
      bin_nos = _.filter(
        this.props.bin_nos,
        b => b !== bin_no
      );
    } else {
      bin_nos = [
        ...this.props.bin_nos,
        bin_no
      ];
    }
    this.props.getChartData({ bin_nos });
  }

  getBinFilterText = () => {
    const { bin_nos } = this.props;
    const { t } = this.props;
    if (bin_nos.length === 0) return '';
    let binFilterText = `${t('machineDashboard.health.charts.filters.speedRange')}: `;
    bin_nos.forEach((bin_no, idx) => {
      if (idx) binFilterText += ',';
      binFilterText += bin_no;
    });
    return binFilterText;
  }

  isOverlayToggleVisible = () => {
    const { chartType, defaultChart, enableOverlayToggle } = this.props;
    const overlayChartTypes = ['spectrum', 'waveform'];
    return overlayChartTypes.indexOf(chartType) !== -1 && defaultChart && enableOverlayToggle;
  }

  render() {
    const {
      bin_nos,
      t,
      calendarSelect,
      calendarExpand,
      calendarExpanded,
      customDayFrom,
      customDayTo,
      selection,
      menuItems,
      viewItems,
      showAnnotationControls,
      defaultChart,
      measurement_type,
      number_of_spectrums,
      handleSpectraChange,
      speedUnits,
      chartType,
      days,
      tag,
      amp_type,
      filterMin,
      filterMax,
      disableFilterOptions,
      disableBinFilter,
      showBaseline,
      loadingWaterfall,
      theme,
      showOverlay,
    } = this.props;
    const {
      ampOptionsExpanded,
      binFilterExpanded,
      measurementOptionsExpanded,
      featureOptionsExpanded,
      speedFilterExpanded,
      deleteTagModal,
      tagToDelete
    } = this.state;

    const baselineButtonDisabled = tag && (!tag.baseline_set || loadingWaterfall);
    const measurementOptionValue = _.find(measurementOptions, option => option.value === measurement_type);
    const measurementTypeKeyMap = {
      2: 'highResolution',
      1: 'fullBandwidth',
      0: 'overalls'
    };
    const measurementOptionText = measurementOptionValue
      ? t(`machineDashboard.health.charts.measurementOptions.${measurementTypeKeyMap[measurementOptionValue.value]}`, {
        defaultValue: measurementOptionValue.text
      })
      : '';

    return (
      <OptionsContainer>
        <ChartHeaderRowContainer>
          <FlexContainer
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            flexBasis={selection ? '33.33%' : '66.66%'}
          >
            <CalendarFilter
              calendarSelect={calendarSelect}
              calendarExpand={calendarExpand}
              calendarExpanded={calendarExpanded}
              disabled={disableFilterOptions}
              days={days}
              customDayFrom={customDayFrom}
              customDayTo={customDayTo}
            />
            {menuItems.map(item => (
              <BtnContainer
                onClick={() => {
                  if (!item.disabled) item.onClick();
                }}
                disabled={item.disabled}
              >
                {getSvg(item.text, item.active, theme)}
              </BtnContainer>
            ))}
            <BorderContainer />
            {defaultChart && tag && tag.structure_type === 'vector' && utils.isDemodSupported(tag.type) && (
              <Filter
                value={chartAmpOrFeatureDisplayName(t, amp_type)}
                open={ampOptionsExpanded}
                setWidth="max-content"
                toggleOpen={() => { if (!disableFilterOptions) this.toggleAmpOptionsExpand(); }}
                disabled={disableFilterOptions}
              >
                {_.map(getTagAmplitudeTypes(tag), ampType => ({
                  text: chartAmpOrFeatureDisplayName(t, ampType),
                  value: ampType
                })).map((option, index) => {
                  const selected = option.value === amp_type;
                  return (
                    <FilterItem
                      key={index.toString()}
                      selected={selected}
                      onClick={() => this.ampOptionSelected(option.value)}
                    >
                      <span>{option.text}</span>
                    </FilterItem>
                  );
                })}
              </Filter>
            )}
            {defaultChart && tag && tag.structure_type === 'vector' && tag.features.length > 0 && chartType !== 'waterfall_spectrum' && this.state.tagFeatures && (
              <Filter
                value={chartAmpOrFeatureDisplayName(t, this.props.feature)}
                open={featureOptionsExpanded}
                disabled={disableFilterOptions}
                toggleOpen={() => { if (!disableFilterOptions) this.toggleFeatureOptionsExpand(); }}
                setWidth="max-content"
              >
                {this.state.tagFeatures.map(feature => (
                  <>
                    <Header>
                      {feature.header === 'Broadband'
                        ? t('machineDashboard.health.charts.featureGroups.broadband', { defaultValue: feature.header })
                        : t('machineDashboard.health.charts.featureGroups.narrowband', { defaultValue: feature.header })}
                    </Header>
                    {feature.children.map(option => (
                      <FilterItem
                        key={option.value}
                        selected={option.value === this.props.feature}
                        onClick={() => this.featureSelected(option.value)}
                      >
                        <span>
                          {chartAmpOrFeatureDisplayName(t, option.value)}
                        </span>
                      </FilterItem>
                    ))}
                  </>
                ))}
              </Filter>
            )}
            {defaultChart && tag && ['vibration', 'current'].includes(tag.type) && (
              <Filter
                value={measurementOptionText}
                open={measurementOptionsExpanded}
                disabled={disableFilterOptions}
                toggleOpen={() => { if (!disableFilterOptions) this.toggleMeasurementOptionsExpand(); }}
                setWidth="max-content"
              >
                {measurementOptions.map((option, index) => {
                  const selected = option.value === measurement_type;
                  if (option.value === 0 && chartType !== 'trend') return null;
                  const optionKey = measurementTypeKeyMap[option.value];
                  const optionText = optionKey
                    ? t(`machineDashboard.health.charts.measurementOptions.${optionKey}`, { defaultValue: option.text })
                    : option.text;
                  return (
                    <FilterItem
                      key={index.toString()}
                      selected={selected}
                      onClick={() => this.measurementOptionSelected(option.value)}
                    >
                      <span>{optionText}</span>
                    </FilterItem>
                  );
                })}
              </Filter>
            )}
            {speedUnits && defaultChart && tag && tag.type === 'vibration' && chartType === 'trend' && (
              <>
                <SpeedFilterCont onClick={() => this.toggleSpeedFilter()}>
                  {getSvg('speedFilter', speedFilterExpanded, theme)}
                </SpeedFilterCont>
                {speedFilterExpanded && (
                  <OutsideAlerter open handleClick={() => this.toggleSpeedFilter()}>
                    <SpeedFilter
                      onSave={this.onSaveSpeedFilter}
                      filterMin={filterMin}
                      filterMax={filterMax}
                      units={speedUnits}
                    />
                  </OutsideAlerter>
                )}
              </>
            )}
            {viewItems.map((item) => {
              if (!item.hidden) {
                let viewKey;
                switch (item.fullText) {
                  case 'Trend':
                    viewKey = 'machineDashboard.health.charts.viewModes.trend';
                    break;
                  case 'Spectrum':
                    viewKey = 'machineDashboard.health.charts.viewModes.spectrum';
                    break;
                  case 'Waveform':
                    viewKey = 'machineDashboard.health.charts.viewModes.waveform';
                    break;
                  case 'Combined (T,S,W)':
                    viewKey = 'machineDashboard.health.charts.viewModes.combined';
                    break;
                  case 'Waterfall Spectrum':
                    viewKey = 'machineDashboard.health.charts.viewModes.waterfallSpectrum';
                    break;
                  case 'Cepstrum':
                    viewKey = 'machineDashboard.health.charts.viewModes.cepstrum';
                    break;
                  default:
                    viewKey = null;
                }
                const viewTitle = viewKey ? t(viewKey) : item.fullText;
                return (
                  <BtnContainer
                    onClick={() => item.onClick()}
                  >
                    <span title={viewTitle}><H4 active={item.active}>{item.text}</H4></span>
                  </BtnContainer>
                );
              }
              return '';
            })}
            {_.some(viewItems.map(item => !item.hidden)) && (<BorderContainer />)}
            {this.props.toggleSplit && (
              <>
                {/* <BorderContainer /> */}
                <BtnContainer
                  onClick={this.props.toggleSplit}
                >
                  <span title={t('charts.chartOptions.splitChart')}><SplitIconSvg active={this.props.split} primaryColor={theme.primaryColor} /></span>
                </BtnContainer>
                <BorderContainer />
              </>
            )}
            {chartType === 'waterfall_spectrum' && (
              <>
                <Label marginBottom="0px" padding="0 0.7em">
                  {t('machineDashboard.health.charts.spectraLabel')}
                </Label>
                <SpectraInputField
                  type="select"
                  options={spectraOptions}
                  value={number_of_spectrums}
                  onChange={handleSpectraChange}
                />
                <BorderContainer />
              </>
            )}
            {tag && (chartType === 'trend' || chartType === 'waterfall_spectrum') && (
              <>
                <BtnContainer
                  onClick={() => { if (!baselineButtonDisabled) this.props.toggleShowBaseline(); }}
                  disabled={baselineButtonDisabled}
                  title={
                    !tag.baseline_set
                      ? t('machineDashboard.health.charts.baselineNotSet')
                      : ''
                  }
                >
                  <Checkbox value={showBaseline && tag.baseline_set} />
                  <Label
                    marginBottom="0px"
                    active={showBaseline}
                    padding="0 0 0 0.7em"
                    disabled={baselineButtonDisabled}
                  >
                    {t('machineDashboard.health.charts.baseline')}
                  </Label>
                </BtnContainer>
                <BorderContainer />
              </>
            )}
            <BtnContainer
              onClick={this.toggleShowOutliers}
            >
              {this.state.showOutliers ? (
                <span title={t('machineDashboard.health.charts.outliersShown')}>
                  <FlexContainer>
                    <Label marginBottom="0px" padding="0 0.5em 0 0"> {t('machineDashboard.health.charts.outliers')} </Label>
                    <ToggleOn />
                  </FlexContainer>
                </span>
              ) : (
                <span title={t('machineDashboard.health.charts.outliersHidden')}>
                  <FlexContainer>
                    <Label marginBottom="0px" padding="0 0.5em 0 0"> {t('machineDashboard.health.charts.outliers')} </Label>
                    <ToggleOff />
                  </FlexContainer>
                </span>
              )}
            </BtnContainer>
            {(['trend', 'spectrum', 'waveform', 'TSW', 'cepstrum'].includes(this.props.chartType)) && this.props.enableAnnotationsToggle !== false && (
              <BtnContainer
                onClick={this.toggleShowAnnotations}
              >
              {this.props.annotationsVisible ? (
                <span title={t('machineDashboard.health.charts.annotationsShown')}>
                  <FlexContainer>
                    <Label marginBottom="0px" padding="0 0.5em 0 0"> {t('machineDashboard.health.charts.annotations')} </Label>
                    <ToggleOn />
                  </FlexContainer>
                </span>
              ) : (
                <span title={t('machineDashboard.health.charts.annotationsHidden')}>
                  <FlexContainer>
                    <Label marginBottom="0px" padding="0 0.5em 0 0"> {t('machineDashboard.health.charts.annotations')} </Label>
                    <ToggleOff />
                  </FlexContainer>
                </span>
              )}
              </BtnContainer>
            )}
            {this.isOverlayToggleVisible() && (
              <BtnContainer
                onClick={this.props.toggleShowOverlay}
              >
                {showOverlay ? (
                  <span title={t('machineDashboard.health.charts.overlayEnabled')}>
                    <FlexContainer>
                      <Label marginBottom="0px" padding="0 0.5em 0 0"> {t('machineDashboard.health.charts.overlay')} </Label>
                      <ToggleOn />
                    </FlexContainer>
                  </span>
                ) : (
                  <span title={t('machineDashboard.health.charts.overlayDisabled')}>
                    <FlexContainer>
                      <Label marginBottom="0px" padding="0 0.5em 0 0"> {t('machineDashboard.health.charts.overlay')} </Label>
                      <ToggleOff />
                    </FlexContainer>
                  </span>
                )}
              </BtnContainer>
            )}
            {tag && tag.type === 'current' && (
              <>
                <BorderContainer />
                <BtnContainer
                  onClick={disableFilterOptions ? () => { } : () => this.props.getChartData({ log_scale: !this.props.logScale })}
                  disabled={disableFilterOptions}
                >
                  {this.props.logScale ? (
                    <span title={t('machineDashboard.health.charts.logScaleSelected')}>
                      <FlexContainer>
                        <Label marginBottom="0px" padding="0 0.5em 0 0"> {t('machineDashboard.health.charts.logScaleLabel')} </Label>
                        <ToggleOn disabled={disableFilterOptions} />
                      </FlexContainer>
                    </span>
                  ) : (
                    <span title={t('machineDashboard.health.charts.logScaleDeselected')}>
                      <FlexContainer>
                        <Label marginBottom="0px" padding="0 0.5em 0 0"> {t('machineDashboard.health.charts.logScaleLabel')} </Label>
                        <ToggleOff disabled={disableFilterOptions} />
                      </FlexContainer>
                    </span>
                  )}
                </BtnContainer>
              </>
            )}
            {defaultChart && tag && tag.active === false && (
              <>
                <BtnContainer
                  onClick={() => this.selectDeleteInactiveTag(tag.id)}
                >
                  <span title={t('machineDashboard.health.charts.deleteTagTooltip')}>
                      <DeleteIconSvg />
                  </span>
                </BtnContainer>
              </>
            )}
          </FlexContainer>
        </ChartHeaderRowContainer>
        <>
          {deleteTagModal && (
            <AlertPrompt
              message={t('machineDashboard.health.charts.deleteTag.message')}
              secondaryMessage={t('machineDashboard.health.charts.deleteTag.secondaryMessage')}
              infoMessage={t('machineDashboard.health.charts.deleteTag.infoMessage')}
              onCancel={() => this.setState({
                deleteTagModal: false
              })}
              onProceed={() => this.deleteTag(tagToDelete)}
            />
          )}
          {defaultChart && tag && tag.type === 'vibration' && tag.no_of_bins && (
            <FlexContainer alignItems="center">
              <BinFilter
                title={t('machineDashboard.health.charts.filters.speedRange')}
                value={this.getBinFilterText()}
                open={binFilterExpanded}
                disabled={disableBinFilter}
                toggleOpen={() => { if (!disableBinFilter) this.toggleBinFilter(); }}
                setWidth="max-content"
              >
                {getBinFilterOptions(tag.no_of_bins, false, t).map((option, index) => {
                  const selected = _.includes(bin_nos, option.value);
                  return (
                    <FilterItem
                      key={index.toString()}
                      selected={selected}
                      onClick={() => this.speedBinSelected(option.value, selected)}
                    >
                      <Checkbox value={selected} />
                      <ListOption selected={selected}>{option.text}</ListOption>
                    </FilterItem>
                  );
                })}
              </BinFilter>
            </FlexContainer>
          )}
        </>
      </OptionsContainer>
    );
  }
}

ChartOptions.propTypes = {
  calendarSelect: PropTypes.func,
  calendarExpand: PropTypes.func,
  calendarExpanded: PropTypes.bool,
  menuItems: PropTypes.array,
  disableBinFilter: PropTypes.bool,
  disableFilterOptions: PropTypes.bool,
  loadingWaterfall: PropTypes.bool,
  showOverlay: PropTypes.bool,
  toggleShowOverlay: PropTypes.func,
  defaultChart: PropTypes.bool,
  enableOverlayToggle: PropTypes.bool,
  annotationsVisible: PropTypes.bool,
  setAnnotationsVisible: PropTypes.func,
  enableAnnotationsToggle: PropTypes.bool,
};

ChartOptions.defaultProps = {
  calendarSelect: () => { },
  calendarExpand: () => { },
  calendarExpanded: false,
  menuItems: [],
  disableBinFilter: false,
  disableFilterOptions: false,
  loadingWaterfall: false,
  showOverlay: false,
  toggleShowOverlay: () => { },
  defaultChart: false,
  enableOverlayToggle: true,
  annotationsVisible: true,
  setAnnotationsVisible: () => { },
  enableAnnotationsToggle: true,
};

const mapStateToProps = state => ({
  currentUser: state.user.user,
  annotationsVisible: getVisible(state)
});

const mapDispatchToProps = dispatch => ({
  assetDetailsActions: bindActionCreators(assetDetailsActions, dispatch),
  hierarchyUpdateActions: bindActionCreators(hierarchyUpdateActions, dispatch),
  setAnnotationsVisible: visible => dispatch(setAnnotationsVisible(visible))
});

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(withTranslation()(ChartOptions)));
